import { Helmet } from "react-helmet";
import { Route, Routes, useLocation } from "react-router-dom";
import { Row, Col } from "antd";
import { integrationService } from "/app/src/services";
import Settings from "/app/src/components/apps/orderBuilder/settings";
import OrderBuilder from "./builder";
import { App, Integration as IntegrationType } from "/app/src/models";
import { useNumberId } from "/app/src/hooks/useNumberId";
import { useQuery } from "@tanstack/react-query";
import OrderBuildOrders from "./orders";
import OrderBuildOrder from "./orders/order";
import { Tabs, Tab } from "@heroui/react";
import { useTranslation } from "react-i18next";
import AutomaticOrderBuildOrder from "./orders/order/automatic";

/**
 * Renders the Integration component, including integration query and tabs for settings, builder, and orders.
 *
 * @param {Object} props - Component properties.
 * @param {App} props.app - The application instance.
 * @returns {JSX.Element} The rendered Integration component.
 */
export default function Integration({ app }: { app: App }) {
  const { integrationId } = useNumberId();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Get integration
  const { data: integration, isFetching } = useQuery({
    queryKey: ["integration", integrationId],
    queryFn: () => {
      return integrationService.getSingle(integrationId);
    },
    enabled: Boolean(integrationId),
    initialData: { integration: {} },
    select: (data: { integration: IntegrationType }) => {
      return data.integration;
    },
  });

  const manualLinks = [
    {
      path: `/apps/${app.id}/integrations/${integration.id}`,
      key: "builder",
    },
    {
      path: `/apps/${app.id}/integrations/${integration.id}/settings`,
      key: "settings",
    },
    {
      path: `/apps/${app.id}/integrations/${integration.id}/orders`,
      key: "orders",
    },
  ];

  const automaticLinks = [
    {
      path: `/apps/${app.id}/integrations/${integration.id}`,
      key: "settings",
    },
    {
      path: `/apps/${app.id}/integrations/${integration.id}/orders`,
      key: "orders",
    },
  ];

  /**
   * Returns the appropriate navigation links based on the integration mode.
   *
   * @returns {{path: string, key: string}[]} The list of links for tabs.
   */
  const getLinks = () => {
    if (integration.number === "manual") {
      return manualLinks;
    }
    return automaticLinks;
  };

  /**
   * Determines the active tab key based on the current pathname.
   *
   * @returns {string} The active tab path.
   */
  const getActiveTabKey = () => {
    const baseOrdersPath = `/apps/${app.id}/integrations/${integration.id}/orders`;
    if (
      pathname === baseOrdersPath ||
      pathname.startsWith(`${baseOrdersPath}/`)
    ) {
      return getLinks().find((link) => link.key === "orders").path;
    }
    return pathname;
  };

  return (
    <div className="integration">
      <Helmet>
        <title>{integration.name}</title>
      </Helmet>

      {!isFetching && (
        <Row gutter={24}>
          <Col span={24}>
            <Tabs
              selectedKey={getActiveTabKey()}
              color="primary"
              classNames={{
                tab: "bg-white px-4 py-2",
                tabList: "bg-white flex",
              }}
              className="pb-[20px]"
            >
              {integration.number === "manual" ? (
                <>
                  {manualLinks.map((item) => (
                    <Tab
                      key={item.path}
                      id={item.key}
                      title={t(`translation:${item.key}`, item.key)}
                      href={item.path}
                    />
                  ))}
                </>
              ) : (
                <>
                  {automaticLinks.map((item) => (
                    <Tab
                      key={item.path}
                      id={item.key}
                      title={t(`translation:${item.key}`, item.key)}
                      href={item.path}
                    />
                  ))}
                </>
              )}
            </Tabs>

            <Routes>
              {integration.number === "manual" ? (
                <>
                  <Route
                    path="/"
                    element={<OrderBuilder integration={integration} />}
                  />
                  <Route
                    path="/settings"
                    element={<Settings integration={integration} />}
                  />
                  <Route
                    path="orders"
                    element={<OrderBuildOrders integration={integration} />}
                  />
                  <Route
                    path="orders/:orderId/*"
                    element={<OrderBuildOrder integration={integration} />}
                  />
                </>
              ) : (
                <>
                  <Route
                    path="/"
                    element={<Settings integration={integration} />}
                  />
                  <Route
                    path="orders"
                    element={<OrderBuildOrders integration={integration} />}
                  />
                  <Route
                    path="orders/:orderId/*"
                    element={<AutomaticOrderBuildOrder />}
                  />
                </>
              )}
            </Routes>
          </Col>
        </Row>
      )}
    </div>
  );
}
